"use client"
import { useState, useEffect, useMemo } from "react"
import { X, Upload, FileText, Loader2, AlertCircle } from "lucide-react"
import { postAws } from "../postAws"
import { postTeacherUploadFileFOrQuestionBankAnswerKey } from "../postFileAws"

/**
 * Upload Answer Key modal (revised)
 * - Dedupes chapter names (trim/collapse whitespace, case-insensitive)
 * - Chapter appears ONCE even if multiple question types exist
 * - After selecting a chapter, shows ALL question types present in that chapter
 * - Allows selecting at most ONE invocation per question type (radio per group)
 * - Submits `invocationIds: [..]` for exactly the chosen types
 * - Works with the "get-all-books" payload used elsewhere in the app
 */
const AddQuestionUploadAnswerkey = ({ onClose }) => {
  // Normalized form values (strings)
  const [formData, setFormData] = useState({
    examType: "", // normalized value
    class: "", // normalized value
    subject: "", // normalized value
    chapter: "", // normalized value
  })

  // Books payload (raw list as returned by API)
  const [books, setBooks] = useState([])
  const [loadingBooks, setLoadingBooks] = useState(true)
  const [booksError, setBooksError] = useState("")

  // Options arrays; each item is { value: normalized, label: display }
  const [options, setOptions] = useState({
    examTypes: [],
    classes: [],
    subjects: [],
    chapters: [],
  })

  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  // Map: { rawQuestionType (normalized): selectedInvocationId }
  const [selectedByType, setSelectedByType] = useState({})

  // ---------- helpers ----------
  const normalize = (s) =>
    (s ?? "")
      .toString()
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase()

  const collapseLabel = (s) => (s ?? "").toString().trim().replace(/\s+/g, " ")

  const toOptions = (strings) => {
    // Return unique-by-normalized array of { value, label }
    const map = new Map()
    for (const raw of strings.filter(Boolean)) {
      const val = normalize(raw)
      if (!map.has(val)) map.set(val, collapseLabel(raw))
    }
    return Array.from(map, ([value, label]) => ({ value, label }))
  }

  const findLabel = (arr, value) => arr.find((o) => o.value === value)?.label || ""

  // Clear per-type selections whenever the filters change
  useEffect(() => {
    setSelectedByType({})
  }, [formData.examType, formData.class, formData.subject, formData.chapter])

  // ---------- load all books (same API as elsewhere) ----------
  useEffect(() => {
    const fetchAllBooks = async () => {
      try {
        setLoadingBooks(true)
        setBooksError("")
        const teacherId = localStorage.getItem("teacherId") || ""
        const classroomId = localStorage.getItem("classRoomId") || ""
        const resp = await postAws(
          "https://api-dev.aurait.ai/QuestionBank/invocation",
          "POST",
          {
            request: "get-all-books",
            body: { teacherId, classroomId },
          },
        )

        const list = resp?.body?.books || []
        setBooks(list)

        // seed exam types (deduped by normalized)
        const examTypes = toOptions(list.map((b) => b.examType))
        setOptions((prev) => ({ ...prev, examTypes, classes: [], subjects: [], chapters: [] }))
        // Reset cascades
        setFormData({ examType: "", class: "", subject: "", chapter: "" })
      } catch (e) {
        console.error("Error fetching books:", e)
        setBooksError("Failed to load books. Please try again.")
      } finally {
        setLoadingBooks(false)
      }
    }

    fetchAllBooks()
  }, [])

  // ---------- recompute cascades ----------
  // Classes depend on examType
  useEffect(() => {
    if (!formData.examType) {
      setOptions((o) => ({ ...o, classes: [], subjects: [], chapters: [] }))
      setFormData((f) => ({ ...f, class: "", subject: "", chapter: "" }))
      return
    }

    const filtered = books.filter((b) => normalize(b.examType) === formData.examType)
    const classes = toOptions(filtered.map((b) => b.class))
    setOptions((o) => ({ ...o, classes, subjects: [], chapters: [] }))
    setFormData((f) => ({ ...f, class: "", subject: "", chapter: "" }))
  }, [formData.examType, books])

  // Subjects depend on examType + class
  useEffect(() => {
    if (!formData.examType || !formData.class) {
      setOptions((o) => ({ ...o, subjects: [], chapters: [] }))
      setFormData((f) => ({ ...f, subject: "", chapter: "" }))
      return
    }

    const filtered = books.filter(
      (b) => normalize(b.examType) === formData.examType && normalize(b.class) === formData.class,
    )
    const subjects = toOptions(filtered.map((b) => b.subject))
    setOptions((o) => ({ ...o, subjects, chapters: [] }))
    setFormData((f) => ({ ...f, subject: "", chapter: "" }))
  }, [formData.class, formData.examType, books])

  // Chapters depend on examType + class + subject
  useEffect(() => {
    if (!formData.examType || !formData.class || !formData.subject) {
      setOptions((o) => ({ ...o, chapters: [] }))
      setFormData((f) => ({ ...f, chapter: "" }))
      return
    }

    const filtered = books.filter(
      (b) =>
        normalize(b.examType) === formData.examType &&
        normalize(b.class) === formData.class &&
        normalize(b.subject) === formData.subject,
    )

    // Dedupe chapters by normalized value so a chapter shows only once (even if multiple types)
    const chapters = toOptions(filtered.map((b) => b.chapter))
    setOptions((o) => ({ ...o, chapters }))
    setFormData((f) => ({ ...f, chapter: "" }))
  }, [formData.subject, formData.class, formData.examType, books])

  const handleChange = (e) => {
    const { name, value } = e.target // value is already normalized (we store normalized in <option value>)
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // ---------- invocations list for the exact selected cascade ----------
  const invocationsForChapter = useMemo(() => {
    if (!formData.examType || !formData.class || !formData.subject || !formData.chapter) return []

    const filtered = (books || []).filter(
      (b) =>
        normalize(b.examType) === formData.examType &&
        normalize(b.class) === formData.class &&
        normalize(b.subject) === formData.subject &&
        normalize(b.chapter) === formData.chapter,
    )

    // sort oldest → newest (stable labeling)
    filtered.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))

    const getRawType = (book) =>
      normalize(
        book.questionType || book.originalQuestionType || book?.summary?.originalQuestionType || book?.summary?.questionType || "",
      )

    return filtered.map((b) => ({
      id: b._id,
      createdAt: b.createdAt,
      rawType: getRawType(b), // normalized type key (e.g., "mcq", "multiplecorrect")
    }))
  }, [books, formData])

  // Group by question type (exact rawType key) and attach human display + per-type labels
  const typeDisplay = (rawType) => {
    const key = normalize(rawType)
    if (key === "mcq") return "MCQ"
    if (key === "multiplecorrect" || key === "multiple-correct" || key === "mcms") return "Multiple Correct"
    if (key === "integertype" || key === "integer") return "Integer Type"
    if (key === "numerical" || key === "numeric") return "Numerical"
    if (!key) return "Unknown"
    // Title-case fallback
    return key.replace(/\b\w+/g, (w) => w[0].toUpperCase() + w.slice(1))
  }

  const chapterLabel = useMemo(
    () => findLabel(options.chapters, formData.chapter),
    [options.chapters, formData.chapter],
  )

  const groupedByType = useMemo(() => {
    const groups = {}

    invocationsForChapter.forEach((iv) => {
      const key = iv.rawType || "__unknown__"
      if (!groups[key]) groups[key] = { display: typeDisplay(key), items: [] }
      groups[key].items.push({ id: iv.id, createdAt: iv.createdAt })
    })

    Object.values(groups).forEach((group) => {
      group.items.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
      group.items = group.items.map((item, idx) => ({
        ...item,
        label: `${chapterLabel}${idx === 0 ? "" : ` ${idx}`}`,
      }))
    })

    return groups
  }, [invocationsForChapter, chapterLabel])

  // Auto-select the only item in a group
  useEffect(() => {
    const next = { ...selectedByType }
    let changed = false
    Object.entries(groupedByType).forEach(([rawType, group]) => {
      if (group.items.length === 1 && !next[rawType]) {
        next[rawType] = group.items[0].id
        changed = true
      }
    })
    if (changed) setSelectedByType(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedByType])

  // ---------- file handlers ----------
  const handleFileInput = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== "application/pdf") {
      alert("Please select a PDF file")
      return
    }
    setSelectedFile(file)
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true)
    if (e.type === "dragleave") setDragActive(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    if (file.type !== "application/pdf") {
      alert("Please select a PDF file")
      return
    }
    setSelectedFile(file)
  }

  // ---------- submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.examType || !formData.class || !formData.subject || !formData.chapter) {
      alert("Please select Exam Type, Class, Subject and Chapter")
      return
    }
    if (!selectedFile) {
      alert("Please select an Answer Key PDF")
      return
    }

    // Collect selected invocation ids (max one per type via radio groups)
    const selectedInvocationIds = Array.from(new Set(Object.values(selectedByType).filter(Boolean)))
    if (selectedInvocationIds.length === 0) {
      alert("Please select at least one invocation for a question type.")
      return
    }

    try {
      setUploading(true)

      // 1) Upload to S3
      const chapterPretty = chapterLabel || findLabel(options.chapters, formData.chapter) || "chapter"
      const filename = `answer-key-${findLabel(options.examTypes, formData.examType) || formData.examType}-${findLabel(options.classes, formData.class) || formData.class}-${findLabel(options.subjects, formData.subject) || formData.subject}-${chapterPretty}-${Date.now()}.pdf`

      const uploadResult = await postTeacherUploadFileFOrQuestionBankAnswerKey(selectedFile, filename)
      const s3uri = uploadResult?.s3uri || uploadResult?.s3Uri || uploadResult?.uri || uploadResult?.url || uploadResult
      if (!s3uri) throw new Error("S3 upload failed")

      // 2) Invoke backend
      const teacherId = localStorage.getItem("teacherId") || ""
      const classroomId = localStorage.getItem("classRoomId") || ""

      const invocationResp = await postAws(
        "https://api-dev.aurait.ai/QuestionBank",
        "POST",
        {
          request: "invoke-upload-answer",
          body: {
            examType: findLabel(options.examTypes, formData.examType),
            class: findLabel(options.classes, formData.class),
            subject: findLabel(options.subjects, formData.subject),
            chapter: chapterPretty,
            s3uri,
            teacherId,
            classroomId,
            filename: selectedFile.name,
            invocationIds: selectedInvocationIds,
          },
        },
      )

      if (invocationResp?.statusCode === 202) {
        alert("Answer key uploaded. Extraction has been started.")
        onClose?.()
      } else {
        console.error("Invocation error:", invocationResp)
        alert("Upload succeeded, but starting extraction failed. Please try again.")
      }
    } catch (err) {
      console.error("Upload/Invoke error:", err)
      alert(err?.message || "Failed to upload answer key. Please try again.")
    } finally {
      setUploading(false)
    }
  }

  // ---------- UI ----------
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Upload Answer Key</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {loadingBooks ? (
            <div className="flex items-center justify-center p-6">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span>Loading your books…</span>
            </div>
          ) : booksError ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="w-5 h-5" />
                <span>{booksError}</span>
              </div>
              <button
                className="mt-3 px-3 py-1 text-sm rounded bg-gray-800 text-white"
                onClick={() => {
                  // quick retry
                  setFormData({ examType: "", class: "", subject: "", chapter: "" })
                  setOptions({ examTypes: [], classes: [], subjects: [], chapters: [] })
                  setLoadingBooks(true)
                  setBooksError("")
                  ;(async () => {
                    try {
                      const teacherId = localStorage.getItem("teacherId") || ""
                      const classroomId = localStorage.getItem("classRoomId") || ""
                      const resp = await postAws(
                        "https://api-dev.aurait.ai/QuestionBank/invocation",
                        "POST",
                        { request: "get-all-books", body: { teacherId, classroomId } },
                      )
                      const list = resp?.body?.books || []
                      setBooks(list)
                      setOptions((prev) => ({
                        ...prev,
                        examTypes: toOptions(list.map((b) => b.examType)),
                      }))
                    } catch (e) {
                      setBooksError("Failed to load books. Please try again.")
                    } finally {
                      setLoadingBooks(false)
                    }
                  })()
                }}
              >
                Retry
              </button>
            </div>
          ) : books.length === 0 ? (
            <div className="p-6 text-center text-gray-600">No books found for your account.</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Exam Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exam Type *</label>
                <select
                  name="examType"
                  value={formData.examType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Exam Type</option>
                  {options.examTypes.map((et) => (
                    <option key={et.value} value={et.value}>
                      {et.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Class */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Class *</label>
                <select
                  name="class"
                  value={formData.class}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                  disabled={!formData.examType || options.classes.length === 0}
                >
                  <option value="">
                    {!formData.examType
                      ? "Select Exam Type first"
                      : options.classes.length === 0
                      ? "No classes available"
                      : "Select Class"}
                  </option>
                  {options.classes.map((cl) => (
                    <option key={cl.value} value={cl.value}>
                      {cl.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                  disabled={!formData.class || options.subjects.length === 0}
                >
                  <option value="">
                    {!formData.class
                      ? "Select Class first"
                      : options.subjects.length === 0
                      ? "No subjects available"
                      : "Select Subject"}
                  </option>
                  {options.subjects.map((sub) => (
                    <option key={sub.value} value={sub.value}>
                      {sub.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Chapter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Chapter *</label>
                <select
                  name="chapter"
                  value={formData.chapter}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                  disabled={!formData.subject || options.chapters.length === 0}
                >
                  <option value="">
                    {!formData.subject
                      ? "Select Subject first"
                      : options.chapters.length === 0
                      ? "No chapters available"
                      : "Select Chapter"}
                  </option>
                  {options.chapters.map((ch) => (
                    <option key={ch.value} value={ch.value}>
                      {ch.label}
                    </option>
                  ))}
                </select>
                {formData.chapter && (
                  <p className="mt-1 text-xs text-gray-500">
                    Showing all question types for <strong>{chapterLabel}</strong>.
                  </p>)
                }
              </div>

              {/* One selection per exact question type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Invocation per Question Type
                </label>

                {!formData.chapter ? (
                  <div className="text-sm text-gray-500">Select a Chapter to view its invocations.</div>
                ) : Object.keys(groupedByType).length === 0 ? (
                  <div className="text-sm text-gray-500">No invocations found for this chapter yet.</div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(groupedByType)
                      .sort((a, b) => a[1].display.localeCompare(b[1].display))
                      .map(([rawType, group]) => (
                        <div key={rawType} className="border rounded-md p-3">
                          <div className="text-sm font-semibold mb-2">{group.display}</div>

                          {/* None option */}
                          <label className="flex items-center gap-2 text-sm mb-1 cursor-pointer">
                            <input
                              type="radio"
                              name={`inv_${rawType}`}
                              value=""
                              checked={!selectedByType[rawType]}
                              onChange={() =>
                                setSelectedByType((prev) => {
                                  const copy = { ...prev }
                                  delete copy[rawType]
                                  return copy
                                })
                              }
                            />
                            <span className="text-gray-600">None</span>
                          </label>

                          {group.items.map((item) => (
                            <label
                              key={item.id}
                              className="flex items-center justify-between text-sm px-2 py-1 rounded hover:bg-gray-50 cursor-pointer"
                            >
                              <span className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={`inv_${rawType}`}
                                  value={item.id}
                                  checked={selectedByType[rawType] === item.id}
                                  onChange={() => setSelectedByType((prev) => ({ ...prev, [rawType]: item.id }))}
                                />
                                <span className="font-medium">{item.label}</span>
                              </span>
                              <span className="text-xs text-gray-400">
                                {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ""}
                              </span>
                            </label>
                          ))}

                          <p className="mt-1 text-xs text-gray-500">
                            Select <strong>one</strong> invocation for this type. You can add/remove selections across types.
                          </p>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Answer Key PDF *</label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive ? "border-orange-500 bg-orange-50" : "border-gray-300 hover:border-orange-400"
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input type="file" accept=".pdf" onChange={handleFileInput} className="hidden" id="file-upload" />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      {selectedFile ? (
                        <span className="text-green-600 font-medium">{selectedFile.name}</span>
                      ) : (
                        <>
                          Click to upload or drag and drop
                          <br />
                          <span className="text-xs text-gray-500">PDF files only</span>
                        </>
                      )}
                    </p>
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" />
                      Upload Answer Key
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default AddQuestionUploadAnswerkey
